import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import useBusinessLocations from '../../hooks/useBusinessLocations';
import useBusinessLocationTypes from '../../hooks/useBusinessLocationTypes';
import GooglePlacesAutocompleteInput from '../../components/common/GooglePlacesAutocompleteInput';
import { addressFieldsFromPlaceDetails } from '../../utils/googlePlaces';
import { uploadFile } from '../../services/fileUploadService';
import { getUser } from '../../services/authService';
import '../../styles/GooglePlacesAutocomplete.css';
import '../../styles/BusinessLocationsPage.css';

interface BusinessLocationsPageProps {
  businessEntityId: string;
}

const emptyContactInformation = {
  primaryEmail: '',
  primaryPhone: '',
  secondaryEmail: '',
  secondaryPhone: '',
  websiteUrl: '',
  contactPersonTitle: '',
  contactPersonName: '',
};

const emptyLocationInformation = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  county: '',
  country: '',
  latitude: '',
  longitude: '',
  placeId: '',
};

const getStoredActorId = () => {
  return getUser()?.id;
};

const BusinessLocationsPage = ({ businessEntityId }: BusinessLocationsPageProps) => {
  const { user } = useAuth();
  const { locations, loading: locationsLoading, list, create, update, remove } =
    useBusinessLocations(businessEntityId);
  const { locationTypes, loading: typesLoading, fetchLocationTypes } = useBusinessLocationTypes();
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchLocationTypes();
    list({ page: 0, size: 50 });
  }, [businessEntityId, fetchLocationTypes, list]);

  const handleAddLocation = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleEditLocation = (location: any) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleSaveLocation = async (locationData: any) => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'You must be signed in to save a location.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      if (editingLocation?.id) {
        await update(editingLocation.id, locationData, user.id);
        setMessage({ type: 'success', text: 'Business location updated successfully.' });
      } else {
        await create(locationData, user.id);
        setMessage({ type: 'success', text: 'Business location created successfully.' });
      }
      setShowForm(false);
      setEditingLocation(null);
      await list({ page: 0, size: 50 });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save business location.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!user?.id || !window.confirm('Remove this business location?')) return;

    setSaving(true);
    setMessage(null);
    try {
      await remove(locationId, user.id);
      setMessage({ type: 'success', text: 'Business location removed successfully.' });
      await list({ page: 0, size: 50 });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to remove business location.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="business-locations-page">
      {message && <div className={`location-message ${message.type}`}>{message.text}</div>}

      <div className="locations-header">
        <h2>Business Locations</h2>
        <button className="btn-add-location" onClick={handleAddLocation} disabled={locationsLoading}>
          + Add Location
        </button>
      </div>

      {showForm ? (
        <div className="location-form-container">
          <button
            className="btn-close-form"
            onClick={() => {
              setShowForm(false);
              setEditingLocation(null);
            }}
          >
            Back
          </button>
          <LocationForm
            initialValues={editingLocation}
            locationTypes={locationTypes}
            loadingTypes={typesLoading}
            onSubmit={handleSaveLocation}
            loading={saving}
          />
        </div>
      ) : (
        <div className="locations-list">
          {locationsLoading ? (
            <p className="no-locations">Loading locations...</p>
          ) : locations.length === 0 ? (
            <p className="no-locations">No business locations found. Create one to get started.</p>
          ) : (
            <div className="locations-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Location Type</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((loc) => (
                    <tr key={loc.id}>
                      <td>{loc.name || '-'}</td>
                      <td>
                        <span className={`status-badge status-${String(loc.status || '').toLowerCase()}`}>
                          {loc.status || '-'}
                        </span>
                      </td>
                      <td>{loc.businessLocationType?.label || loc.businessLocationType?.code || '-'}</td>
                      <td>
                        {loc.locationInformation?.addressLine1 ||
                          [loc.locationInformation?.city, loc.locationInformation?.country].filter(Boolean).join(', ') ||
                          '-'}
                      </td>
                      <td>{loc.contactInformation?.primaryPhone || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-edit" onClick={() => handleEditLocation(loc)}>Edit</button>
                          <button className="btn-delete" onClick={() => handleDeleteLocation(loc.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface LocationFormProps {
  initialValues?: any;
  locationTypes: any[];
  loadingTypes: boolean;
  onSubmit: (data: any) => Promise<void>;
  loading: boolean;
}

const LocationForm = ({ initialValues, locationTypes, loadingTypes, onSubmit, loading }: LocationFormProps) => {
  const { user } = useAuth();
  const { googleMapsApiKey } = useAppConfig();
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'ACTIVE',
    description: '',
    businessLocationType: '',
    profilePhoto: '',
    contactInformation: emptyContactInformation,
    locationInformation: emptyLocationInformation,
  });

  useEffect(() => {
    if (!initialValues) {
      setFormData({
        name: '',
        status: 'ACTIVE',
        description: '',
        businessLocationType: '',
        profilePhoto: '',
        contactInformation: emptyContactInformation,
        locationInformation: emptyLocationInformation,
      });
      setPlaceQuery('');
      return;
    }

    const nextLocation = {
      ...emptyLocationInformation,
      ...(initialValues.locationInformation || {}),
    };
    setFormData({
      name: initialValues.name || '',
      status: initialValues.status || 'ACTIVE',
      description: initialValues.description || '',
      businessLocationType:
        initialValues.businessLocationType?.id ||
        initialValues.businessLocationTypeId ||
        initialValues.businessLocationType ||
        '',
      profilePhoto: initialValues.profilePhoto || '',
      contactInformation: {
        ...emptyContactInformation,
        ...(initialValues.contactInformation || {}),
      },
      locationInformation: nextLocation,
    });
    setPlaceQuery(nextLocation.addressLine1 || '');
  }, [initialValues]);

  const setContactField = (name: keyof typeof emptyContactInformation, value: string) => {
    setFormData((prev) => ({
      ...prev,
      contactInformation: { ...prev.contactInformation, [name]: value },
    }));
  };

  const uploadProfilePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const actorId = user?.id || getStoredActorId();
    if (!actorId) {
      setFormMessage({ type: 'error', text: 'You must be signed in to upload a location photo.' });
      return;
    }

    setUploadingPhoto(true);
    setFormMessage(null);
    try {
      const result = await uploadFile(actorId, file, { purpose: 'LOCATION_PHOTO', fileType: 'PHOTO', confirm: false });
      setFormData((prev) => ({ ...prev, profilePhoto: result.fileId }));
      setFormMessage({ type: 'success', text: 'Profile photo uploaded.' });
    } catch (error: any) {
      setFormMessage({ type: 'error', text: error?.message || 'Failed to upload profile photo.' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormMessage(null);

    if (!formData.name.trim()) {
      setFormMessage({ type: 'error', text: 'Location name is required.' });
      return;
    }

    if (!formData.businessLocationType) {
      setFormMessage({ type: 'error', text: 'Location type is required.' });
      return;
    }

    await onSubmit({
      name: formData.name.trim(),
      status: formData.status,
      description: formData.description.trim() || null,
      businessLocationType: formData.businessLocationType,
      profilePhoto: formData.profilePhoto || null,
      contactInformation: formData.contactInformation,
      locationInformation: {
        ...formData.locationInformation,
        latitude: formData.locationInformation.latitude ? Number(formData.locationInformation.latitude) : undefined,
        longitude: formData.locationInformation.longitude ? Number(formData.locationInformation.longitude) : undefined,
      },
    });
  };

  return (
    <form className="location-form" onSubmit={handleSubmit}>
      {formMessage && <div className={`form-message ${formMessage.type}`}>{formMessage.text}</div>}

      <div className="form-group">
        <label>Location Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
        />
      </div>

      <div className="form-group">
        <label>Status</label>
        <select value={formData.status} onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="DELETED">Deleted</option>
        </select>
      </div>

      <div className="form-group">
        <label>Location Type *</label>
        <select
          value={formData.businessLocationType}
          onChange={(e) => setFormData((prev) => ({ ...prev, businessLocationType: e.target.value }))}
          disabled={loadingTypes}
          required
        >
          <option value="">{loadingTypes ? 'Loading...' : 'Select type'}</option>
          {locationTypes.map((type) => (
            <option key={type.id} value={type.id}>{type.label} ({type.code})</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Profile Photo</label>
        <button type="button" className="btn-upload-inline" onClick={() => profilePhotoInputRef.current?.click()} disabled={uploadingPhoto}>
          {formData.profilePhoto ? 'Change profile photo' : 'Upload profile photo'}
        </button>
        <input ref={profilePhotoInputRef} type="file" accept="image/*" hidden onChange={uploadProfilePhoto} />
      </div>

      <div className="form-group form-span-2">
        <label>Description</label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
        />
      </div>

      {Object.entries(formData.contactInformation).map(([key, value]) => (
        <div className="form-group" key={key}>
          <label>{key.replace(/([A-Z])/g, ' $1')}</label>
          <input value={value} onChange={(e) => setContactField(key as keyof typeof emptyContactInformation, e.target.value)} />
        </div>
      ))}

      <div className="form-group form-span-2">
        <label>Search Address</label>
        <GooglePlacesAutocompleteInput
          googleMapsApiKey={googleMapsApiKey}
          placeholder="Search for an address"
          value={placeQuery}
          onChange={setPlaceQuery}
          onResolvePlace={({ details }) => {
            const next = addressFieldsFromPlaceDetails(details);
            setFormData((prev) => ({
              ...prev,
              locationInformation: {
                ...prev.locationInformation,
                addressLine1: next.addressLine1,
                city: next.city,
                state: next.state,
                zipCode: next.zipCode,
                county: next.county,
                country: next.country,
                latitude: next.latitude !== undefined ? String(next.latitude) : '',
                longitude: next.longitude !== undefined ? String(next.longitude) : '',
                placeId: next.placeId,
              },
            }));
          }}
        />
        <p className="hint">Search and select an address to auto-fill the fields below, or enter the location manually.</p>
      </div>

      {Object.entries(formData.locationInformation).map(([key, value]) => (
        <div className="form-group" key={key}>
          <label>{key.replace(/([A-Z])/g, ' $1')}</label>
          <input
            value={value}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                locationInformation: { ...prev.locationInformation, [key]: e.target.value },
              }))
            }
          />
        </div>
      ))}

      <button type="submit" className="btn-save" disabled={loading || uploadingPhoto}>
        {loading ? 'Saving...' : 'Save Location'}
      </button>
    </form>
  );
};

export default BusinessLocationsPage;
